import React, { useEffect, useRef, useState } from "react";
import { postLoginPath } from "@/lib/adminScope";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  X, Mail, LockKeyhole, User, Sparkles, ShieldCheck, KeyRound, ArrowLeft, Smartphone, UserCheck, UserRound, ChevronRight,
} from "lucide-react";
import BrandName from "@/components/BrandName";
import { useAuth, readReturningHint, forgetReturning } from "@/lib/auth";
import { api } from "@/lib/api";
import "./AuthModal.css";

const STAR = "M0 -10 L2.2 -2.2 L10 0 L2.2 2.2 L0 10 L-2.2 2.2 L-10 0 L-2.2 -2.2 Z";

/* ------------------------------------------------------------------ */
/*  PIN-first sign-in                                                  */
/*    Log in  : mobile number (or e-mail) + 4-digit PIN                */
/*    Sign up : name + mobile + e-mail, then create a 4-digit PIN      */
/*    Forgot  : e-mail code signs in (secondary), then set a new PIN   */
/*  One customer account per MOBILE number; the e-mail is not unique.  */
/*  A returning customer sees a personal welcome with their name.      */
/* ------------------------------------------------------------------ */
const RESEND_SECONDS = 45;
const EMAIL_RE = /^\S+@\S+\.\S+$/;
const WEAK_PINS = new Set(["0000", "1111", "2222", "3333", "4444", "5555", "6666", "7777", "8888", "9999", "1234", "4321", "0123", "3210", "2580", "1212", "2020"]);

const normPhone = (raw) => {
  let d = String(raw || "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return /^[6-9]\d{9}$/.test(d) ? d : "";
};
const maskPhone = (p) => { const d = String(p || "").replace(/\D/g, "").slice(-10); return d.length === 10 ? `${d.slice(0, 2)}${"•".repeat(6)}${d.slice(-2)}` : "••••••••••"; };
const maskEmail = (e) => { const [u = "", d = ""] = String(e || "").split("@"); return u && d ? `${u.slice(0, 2)}${"•".repeat(Math.max(2, Math.min(6, u.length - 2)))}@${d}` : ""; };
const looksLikeLogin = (v) => EMAIL_RE.test(v.trim()) || !!normPhone(v);
const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };
const reducedMotion = () => { try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; } };

function ResendRing({ left, total }) {
  const r = 9, c = 2 * Math.PI * r;
  return (
    <svg className="nw-ring" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r={r} className="nw-ring-bg" />
      <circle cx="12" cy="12" r={r} className="nw-ring-fg" style={{ strokeDasharray: c, strokeDashoffset: c * (1 - left / total) }} />
    </svg>
  );
}

/** Types a list of lines one after another (typewriter), for the animated text under the car scene. */
function useTypewriter(lines, active) {
  const key = lines.join("|");
  const [text, setText] = useState(lines[0] || "");
  useEffect(() => {
    if (!active) return undefined;
    if (reducedMotion()) { setText(lines[0] || ""); return undefined; }
    let li = 0, ci = 0, dir = 1, timer = 0;
    const tick = () => {
      const line = lines[li];
      if (dir === 1) {
        ci += 1; setText(line.slice(0, ci));
        if (ci >= line.length) { dir = 0; timer = window.setTimeout(tick, 1900); return; }
        timer = window.setTimeout(tick, 42);
      } else if (dir === 0) { dir = -1; timer = window.setTimeout(tick, 30); }
      else {
        ci -= 2;
        if (ci <= 0) { ci = 0; setText(""); li = (li + 1) % lines.length; dir = 1; timer = window.setTimeout(tick, 320); return; }
        setText(line.slice(0, ci)); timer = window.setTimeout(tick, 16);
      }
    };
    setText(""); timer = window.setTimeout(tick, 350);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, key]);
  return text;
}

/** Four PIN boxes backed by one hidden input (so the phone's number keyboard, paste and autofill all work). */
const PinPad = React.forwardRef(function PinPad({ value, onChange, disabled, shake, label, testId, autoFocus }, ref) {
  const inputRef = useRef(null);
  React.useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }));
  useEffect(() => { if (autoFocus) { const t = window.setTimeout(() => inputRef.current?.focus(), 120); return () => window.clearTimeout(t); } return undefined; }, [autoFocus]);
  // `shake` is a counter; the red "wrong PIN" look lasts only for the length of the animation.
  const [shaking, setShaking] = useState(false);
  useEffect(() => { if (!shake) return undefined; setShaking(true); const t = window.setTimeout(() => setShaking(false), 650); return () => window.clearTimeout(t); }, [shake]);
  return (
    <div className={`nw-pin${shaking ? " is-shake" : ""}`} onClick={() => inputRef.current?.focus()} data-testid={testId}>
      <input ref={inputRef} className="nw-pin-hidden" inputMode="numeric" pattern="[0-9]*" maxLength={4} autoComplete="off" aria-label={label || "4-digit PIN"}
        value={value} disabled={disabled} onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid={testId ? `${testId}-input` : undefined} />
      <div className="nw-pin-boxes" key={shake}>
        {[0, 1, 2, 3].map((i) => {
          const filled = i < value.length;
          const active = i === value.length && !disabled;
          return (
            <span key={i} className={`nw-pin-box${filled ? " is-filled" : ""}${active ? " is-active" : ""}`} aria-hidden="true">
              {filled ? <i className="nw-pin-dot" /> : null}
            </span>
          );
        })}
      </div>
    </div>
  );
});

/** Customer name from the database: each letter drops in with its own purple-to-rainbow colour, and the colours keep drifting. */
function WaveName({ name }) {
  const chars = [...name];
  return (
    <span className="nw-wave" aria-label={name}>
      {chars.map((ch, i) => (
        <span key={`${ch}${i}`} aria-hidden="true" style={{ "--h": 255 + Math.round((i / Math.max(1, chars.length - 1)) * 95), animationDelay: `${0.2 + i * 0.07}s` }}>{ch}</span>
      ))}
    </span>
  );
}

/** Avatar with a ring that fills as the PIN is typed. */
function AvatarRing({ name, progress, state }) {
  const r = 46, c = 2 * Math.PI * r;
  return (
    <div className={`nw-avatar is-${state}`} aria-hidden="true">
      <svg viewBox="0 0 108 108">
        <circle cx="54" cy="54" r={r} className="nw-avatar-track" />
        <circle cx="54" cy="54" r={r} className="nw-avatar-fill" style={{ strokeDasharray: c, strokeDashoffset: c * (1 - progress) }} />
      </svg>
      <b>{(name || "?").charAt(0).toUpperCase()}</b>
    </div>
  );
}

export default function AuthModal({ open, mode = "signin", onClose, onModeChange, referralCode = "", initialEmail = "", attribution = {} }) {
  const navigate = useNavigate();
  const { requestOtp, verifyOtp, loginPin, login, register, savePin, googleLogin } = useAuth();

  // view: welcome (returning customer) | login | signup | signupPin | code | newPin
  const [view, setView] = useState("login");
  const [hint, setHint] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState(initialEmail || "");
  const [identifier, setIdentifier] = useState("");
  const [pin, setPin] = useState("");
  const [pinPhase, setPinPhase] = useState("create"); // create | confirm  (signupPin and newPin)
  const [firstPin, setFirstPin] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // null | "login" | "signup"
  const [cooldown, setCooldown] = useState(0);
  const [codeIntent, setCodeIntent] = useState("forgot"); // "login" = e-mail code is just another way in | "forgot" = then choose a new PIN
  const [lookingUp, setLookingUp] = useState(false);       // automatic account check in progress
  const [notFound, setNotFound] = useState(false);
  const [shake, setShake] = useState(0);
  const [exists, setExists] = useState(null); // { masked, phone } when the mobile number already has an account
  const [setupToken, setSetupToken] = useState("");
  const [usePassword, setUsePassword] = useState(false); // admin accounts only
  const [password, setPassword] = useState("");

  const googleBtnRef = useRef(null);
  const googleCbRef = useRef(null);
  const codeRef = useRef(null);
  const pinRef = useRef(null);
  const timerRef = useRef(0);
  const landingRef = useRef("");
  const submittingRef = useRef(false); // blocks double-submits
  const lookupSeq = useRef(0);          // newest account check wins
  const [googleClientId, setGoogleClientId] = useState(process.env.REACT_APP_GOOGLE_CLIENT_ID || "");

  const busy = loading || !!done;
  const welcome = view === "welcome" && !!hint;
  const isSignup = view === "signup" || view === "signupPin";
  const first = hint?.name || "";

  const lines = React.useMemo(() => (hint?.name
    ? [`${hint.name}, your car missed you.`, "Fresh foam is already waiting.", "Your next shine is one PIN away.", "Doorstep care, royal finish."]
    : ["Luxury car care, at your doorstep."]), [hint?.name]);
  const showName = !!hint?.name && (view === "welcome" || view === "code" || view === "newPin");
  const typed = useTypewriter(lines, open && showName);

  // A fresh open starts clean. A customer who signed in on this device before gets the personal welcome screen.
  useEffect(() => {
    if (!open) return;
    let h = null;
    try { h = readReturningHint(); } catch { /* ignore */ }
    const customerHint = h && h.name && (h.identifier || h.email) && String(h.role || "customer") === "customer" ? { ...h, identifier: h.identifier || h.email } : null;
    setHint(customerHint);
    setView(mode === "signup" ? "signup" : customerHint ? "welcome" : "login");
    setDone(null); setLoading(false); setCode(""); setPin(""); setFirstPin(""); setPinPhase("create"); setError(""); setInfo(""); setExists(null); setSetupToken("");
    submittingRef.current = false;
    if (initialEmail) setEmail((cur) => cur || initialEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialEmail]);

  // The sign-up / log-in switch from the landing page (e.g. "Get started") is honoured while the popup is open too.
  useEffect(() => {
    if (!open || busy) return;
    if (mode === "signup" && !(view === "signup" || view === "signupPin")) { setView("signup"); setError(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Lock page scroll while the popup is open.
  useEffect(() => {
    if (!open) return undefined;
    const body = document.body;
    const prev = { overflow: body.style.overflow, pr: body.style.paddingRight };
    const sw = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    if (sw > 0) body.style.paddingRight = `${sw}px`;
    return () => { body.style.overflow = prev.overflow === "hidden" ? "" : prev.overflow; body.style.paddingRight = prev.pr; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !busy) { if (exists) setExists(null); else onClose?.(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose, exists]);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  // Resend countdown
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = window.setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  // Focus the right box when a step appears
  useEffect(() => { if (view === "code") window.setTimeout(() => codeRef.current?.focus(), 250); }, [view]);
  useEffect(() => { if (view === "signupPin" || view === "newPin") window.setTimeout(() => pinRef.current?.focus(), 150); }, [view]);

  // Google client id (build env first, backend second). Google stays optional and only appears when configured.
  useEffect(() => {
    if (!open || googleClientId) return;
    api.get("/auth/google/config").then(({ data }) => { if (data?.client_id) setGoogleClientId(data.client_id); }).catch(() => {});
  }, [open, googleClientId]);

  const finish = (kind = "login") => {
    setDone(kind);
    timerRef.current = window.setTimeout(() => {
      try { document.body.style.overflow = ""; document.body.style.paddingRight = ""; document.documentElement.style.overflow = ""; } catch { /* ignore */ }
      navigate(landingRef.current || "/dashboard");
    }, 1300);
  };

  googleCbRef.current = async (response) => {
    try {
      landingRef.current = postLoginPath(await googleLogin(response.credential, attribution));
      toast.success("Signed in with Google");
      finish();
    } catch (err) {
      setError(err?.response?.data?.detail || "Google sign-in failed. Please use your mobile number instead.");
    }
  };

  useEffect(() => {
    if (!open || !(view === "login" || view === "signup") || !googleClientId) return undefined;
    const scriptId = "google-identity-services";
    const init = () => {
      if (!window.google?.accounts?.id || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({ client_id: googleClientId, callback: (r) => googleCbRef.current?.(r) });
      googleBtnRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "filled_black", size: "large", shape: "pill",
        width: Math.min(320, Math.max(220, (googleBtnRef.current.parentElement?.clientWidth || 320))),
        text: view === "signup" ? "signup_with" : "signin_with",
      });
    };
    const existing = document.getElementById(scriptId);
    if (existing) {
      if (window.google?.accounts?.id) init(); else existing.addEventListener("load", init, { once: true });
      return undefined;
    }
    const script = document.createElement("script");
    script.id = scriptId; script.src = "https://accounts.google.com/gsi/client"; script.async = true; script.defer = true; script.onload = init;
    document.body.appendChild(script);
    return undefined;
  }, [open, view, googleClientId]);

  const explain = (err, fallback) => {
    if (!err?.response) return "Cannot reach the server. Check your internet and try again.";
    const d = err.response.data?.detail;
    if (d && typeof d === "object") return d.message || fallback;
    return d || fallback;
  };

  const go = (next) => { setView(next); setError(""); setInfo(""); setPin(""); setFirstPin(""); setPinPhase("create"); setExists(null); };
  const switchMode = (next) => {
    go(next === "signup" ? "signup" : hint ? "welcome" : "login");
    onModeChange?.(next);
  };
  const shakePin = () => { setShake((n) => n + 1); setPin(""); window.setTimeout(() => pinRef.current?.focus(), 60); };

  /* ----- log in with PIN ----- */
  const loginIdent = view === "welcome" ? (hint?.identifier || "")
    : (view === "code" && hint && !identifier.trim()) ? hint.identifier : identifier.trim();

  const submitLogin = async (value) => {
    if (submittingRef.current) return;
    if (!looksLikeLogin(loginIdent)) { setError("Enter your 10-digit mobile number first."); setPin(""); return; }
    if (!/^\d{4}$/.test(value)) return;
    submittingRef.current = true; setLoading(true); setError("");
    try {
      landingRef.current = postLoginPath(await loginPin(loginIdent, value));
      toast.success(first ? `Welcome back, ${first}` : "Welcome back");
      finish("login");
    } catch (err) {
      setError(explain(err, "Wrong mobile number or PIN."));
      shakePin();
    } finally { setLoading(false); submittingRef.current = false; }
  };

  const onLoginPin = (v) => { setPin(v); setError(""); if (v.length === 4) submitLogin(v); };

  // Passwords exist for ADMIN accounts only (the server refuses them for anyone else); everybody else uses the PIN.
  const submitPassword = async (e) => {
    e.preventDefault();
    if (submittingRef.current || !password) return;
    submittingRef.current = true; setLoading(true); setError("");
    try {
      landingRef.current = postLoginPath(await login(hint?.identifier || "", password));
      toast.success(first ? `Welcome back, ${first}` : "Welcome back");
      finish("login");
    } catch (err) { setError(explain(err, "Wrong password.")); setPassword(""); }
    finally { setLoading(false); submittingRef.current = false; }
  };

  /* ----- step 1: mobile / e-mail -> greet the account holder by name (from the database), hide what was typed -----
   * Runs by itself as soon as a full mobile number / e-mail has been typed (silent = no button press, no error flash),
   * and also from the Continue button. Only the newest answer is used. */
  const lookup = async (e, override, silent = false) => {
    e?.preventDefault?.();
    const id = (override || identifier).trim();
    if (!looksLikeLogin(id)) { if (!silent) setError("Enter your 10-digit mobile number or e-mail."); return; }
    if (!silent && submittingRef.current) return;
    const seq = ++lookupSeq.current;
    if (silent) setLookingUp(true); else { submittingRef.current = true; setLoading(true); setLookingUp(false); }
    setError(""); setNotFound(false);
    try {
      const { data } = await api.post("/auth/lookup", { identifier: id.includes("@") ? id.toLowerCase() : id });
      if (seq !== lookupSeq.current) return; // a newer check replaced this one
      if (data?.ambiguous) setError("More than one account uses this e-mail. Please enter your mobile number.");
      else if (!data?.found) { if (silent) setNotFound(true); else setError("We couldn't find an account with that. Check it, or create a new account."); }
      else {
        // the typed value moves out of sight; the person sees their own name and a masked contact instead
        setHint({ name: data.name || "there", identifier: id, masked: data.masked, maskedEmail: data.masked_email, role: data.role, hasPin: data.has_pin, passwordAllowed: !!data.password_allowed, viaLookup: true });
        setIdentifier(""); setPin(""); setPassword(""); setUsePassword(false); setView("welcome");
      }
    } catch (err) { if (seq === lookupSeq.current && !silent) setError(explain(err, "Could not check that. Try again.")); }
    finally {
      if (silent) { if (seq === lookupSeq.current) setLookingUp(false); }
      else { setLoading(false); submittingRef.current = false; }
    }
  };

  useEffect(() => {
    if (!open || view !== "login" || exists) return undefined;
    const id = identifier.trim();
    setNotFound(false);
    if (!looksLikeLogin(id)) { lookupSeq.current += 1; setLookingUp(false); return undefined; }
    // a mobile number is complete at 10 digits; an e-mail gets a little longer so a half-typed address is not checked
    const t = window.setTimeout(() => lookup(null, id, true), id.includes("@") ? 600 : 200);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, view, open, exists]);

  /* ----- forgot PIN: e-mail code (secondary way in) ----- */
  const sendCode = async (e, intent = "forgot") => {
    e?.preventDefault?.();
    if (submittingRef.current) return;
    if (!looksLikeLogin(loginIdent)) { setError("Enter your mobile number, then choose Forgot PIN."); return; }
    submittingRef.current = true; setLoading(true); setError(""); setInfo("");
    try {
      const res = await requestOtp(loginIdent);
      setCodeIntent(intent);
      setCode(""); setPin(""); setView("code"); setCooldown(res?.resend_after_seconds || RESEND_SECONDS);
      const to = hint?.maskedEmail || (hint?.email ? maskEmail(hint.email) : "");
      setInfo(to ? `We sent a 6-digit code to ${to}.` : "If an account matches, we sent a 6-digit code to its e-mail.");
    } catch (err) { setError(explain(err, "Could not send the code. Try again.")); }
    finally { setLoading(false); submittingRef.current = false; }
  };

  const resend = async () => {
    if (cooldown > 0 || submittingRef.current) return;
    submittingRef.current = true; setLoading(true); setError("");
    try { const res = await requestOtp(loginIdent); setCode(""); setCooldown(res?.resend_after_seconds || RESEND_SECONDS); setInfo("A new code is on its way."); }
    catch (err) { setError(explain(err, "Could not resend the code.")); }
    finally { setLoading(false); submittingRef.current = false; }
  };

  const verify = async (value = code) => {
    if (submittingRef.current) return;
    if (!/^\d{6}$/.test(value)) { setError("Enter the 6-digit code from your e-mail."); return; }
    submittingRef.current = true; setLoading(true); setError("");
    try {
      const res = await verifyOtp(loginIdent, value);
      landingRef.current = postLoginPath(res.user);
      setSetupToken(res.setupToken);
      if (codeIntent === "login" && res.hasPin) { // the e-mail code was simply another way in: no PIN change needed
        toast.success(first ? `Welcome back, ${first}` : "Signed in");
        finish("login");
        return;
      }
      toast.success("Signed in");
      setPin(""); setFirstPin(""); setPinPhase("create"); setView("newPin"); // a forgotten PIN is replaced right now
    } catch (err) { setError(explain(err, "That code did not work. Request a new one.")); setCode(""); codeRef.current?.focus(); }
    finally { setLoading(false); submittingRef.current = false; }
  };

  const onCodeChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(v); setError("");
    if (v.length === 6) verify(v); // auto-verify once all six digits are in (also covers OS autofill)
  };

  /* ----- create a PIN (sign-up, or a new PIN after the e-mail code): enter it twice ----- */
  const onNewPin = async (v) => {
    setPin(v); setError("");
    if (v.length !== 4 || submittingRef.current) return;
    if (pinPhase === "create") {
      if (WEAK_PINS.has(v)) { setError("That PIN is too easy to guess. Avoid repeated or sequential digits."); shakePin(); return; }
      window.setTimeout(() => { setFirstPin(v); setPin(""); setPinPhase("confirm"); }, 260);
      return;
    }
    if (v !== firstPin) { setError("The two PINs did not match. Let's try again."); setFirstPin(""); setPinPhase("create"); shakePin(); return; }
    submittingRef.current = true; setLoading(true);
    try {
      if (view === "signupPin") {
        const user = await register(name.trim(), email.trim().toLowerCase(), normPhone(phone), v, referralCode, attribution);
        landingRef.current = postLoginPath(user);
        toast.success("Account created");
        finish("signup");
      } else {
        await savePin(v, setupToken);
        toast.success("New PIN saved");
        finish("login");
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "phone_exists") {
        setExists({ masked: d.masked_phone || maskPhone(phone), phone: normPhone(phone) });
        setView("signup"); setPin(""); setFirstPin(""); setPinPhase("create");
      } else {
        setError(explain(err, "Could not save your PIN. Try again."));
        if (view === "signupPin" && err?.response?.status === 400) { setFirstPin(""); setPinPhase("create"); }
        shakePin();
      }
    } finally { setLoading(false); submittingRef.current = false; }
  };

  const signupDetails = (e) => {
    e.preventDefault();
    if (name.trim().length < 2) { setError("Enter your full name."); return; }
    if (!normPhone(phone)) { setError("Enter a valid 10-digit mobile number."); return; }
    if (!EMAIL_RE.test(email.trim())) { setError("Enter a valid e-mail address."); return; }
    setError(""); setPin(""); setFirstPin(""); setPinPhase("create"); setView("signupPin");
  };

  const onPhoneChange = (e) => {
    let d = e.target.value.replace(/\D/g, "");
    if (d.length > 10 && d.startsWith("91")) d = d.slice(2);
    if (d.length > 10 && d.startsWith("0")) d = d.slice(1);
    setPhone(d.slice(0, 10)); setError("");
  };

  const logInWithExisting = () => {
    const p = exists?.phone || normPhone(phone);
    setExists(null); setPin(""); setError(""); setIdentifier(p); onModeChange?.("signin");
    lookup(null, p); // straight to "Welcome back, <name>" for that number
    window.setTimeout(() => pinRef.current?.focus(), 450);
  };

  if (!open) return null;

  /* ----- copy for each screen ----- */
  const pinLen = pin.length;
  const stepTalk = ["", "Nice start", "Halfway there", "One more digit", "Got it"][pinLen] || "";
  const liveLine = done ? "" : error ? error
    : loading ? (view === "signupPin" ? "Creating your account…" : view === "newPin" ? "Saving your PIN…" : "Unlocking your garage…")
    : view === "welcome" ? (pinLen ? `${stepTalk}, ${first}` : `${first}, enter your PIN`)
    : (pinLen ? stepTalk : "Enter your 4-digit PIN");

  const copy = {
    login: { eyebrow: "WELCOME BACK", title: "Your car missed you.", sub: "Enter your mobile number, then your 4-digit PIN." },
    signup: { eyebrow: "JOIN THE CLUB", title: "Start the royal treatment.", sub: referralCode ? "You're joining through a NAVI referral." : "One account per mobile number. You'll create a 4-digit PIN next. No password needed." },
    signupPin: { eyebrow: "ALMOST THERE", title: pinPhase === "create" ? "Create your PIN." : "Confirm your PIN.", sub: pinPhase === "create" ? "Pick 4 digits you'll remember. This is how you'll log in from now on." : "Type the same 4 digits once more." },
    newPin: { eyebrow: "SIGNED IN", title: pinPhase === "create" ? "Set a new PIN." : "Confirm new PIN.", sub: pinPhase === "create" ? "You're in. Choose a new 4-digit PIN so next time is one tap." : "Type the same 4 digits once more." },
  };

  return createPortal(
    <div className="nw-auth-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }} data-testid="auth-modal">
      <div className={`nw-auth-card${welcome ? " is-welcome" : ""}`} role="dialog" aria-modal="true" aria-label="Sign in to NAVI CARE">

        <section className="nw-auth-form">
          <button type="button" className="nw-close" onClick={() => !busy && onClose?.()} aria-label="Close" data-testid="auth-close"><X className="w-4 h-4" /></button>
        <aside className={`nw-auth-show${showName ? " has-name" : ""}`}>
          <div className="nw-logo" data-testid="auth-logo"><BrandName services shine style={{ fontSize: "2.2rem" }} /></div>
          <div className="nw-hero" aria-live="polite">
            {showName ? (
              <div className="nw-talk" data-testid="welcome-talk" key={first}>
                <div className="nw-talk-hi">{greeting()},</div>
                <div className="nw-talk-name"><WaveName name={first} /></div>
                <p className="nw-typed"><span>{typed}</span><i className="nw-caret" /></p>
              </div>
            ) : lookingUp ? (
              <div className="nw-talk is-seeking" data-testid="seeking-talk">
                <div className="nw-talk-hi">One moment</div>
                <div className="nw-seek" aria-hidden="true"><i /><i /><i /></div>
                <p className="nw-typed">Finding your account…</p>
              </div>
            ) : (
              <div className="nw-talk is-generic">
                <div className="nw-talk-hi">{isSignup ? "Join us" : "Welcome"}</div>
                <div className="nw-talk-name nw-generic"><span className="nw-rainbow">{isSignup ? "Let's get you started" : "Sign in to your garage"}</span></div>
                <p className="nw-typed">{isSignup ? "One account per mobile number." : "Type your mobile number. We find you and say hello."}</p>
              </div>
            )}
          </div>
        </aside>

          {exists ? (
            <div className="nw-exists-scrim" role="alertdialog" aria-modal="true" aria-labelledby="nw-exists-title" data-testid="phone-exists-popup">
              <div className="nw-exists">
                <span className="nw-exists-ico"><UserCheck className="w-7 h-7" /></span>
                <h3 id="nw-exists-title">You already have an account</h3>
                <p>The mobile number <b>+91 {exists.masked}</b> is already registered with NAVI CARE. There is one account per mobile number, so just log in with your PIN.</p>
                <button type="button" className="nw-cta" onClick={logInWithExisting} data-testid="phone-exists-login"><span>Log in</span></button>
                <button type="button" className="nw-ghost nw-exists-alt" onClick={() => { setExists(null); }} data-testid="phone-exists-other">Use a different number</button>
              </div>
            </div>
          ) : null}

          {done ? (
            <div className="nw-done" role="status">
              <svg viewBox="0 0 120 120" className="nw-done-svg" aria-hidden="true">
                <circle cx="60" cy="60" r="44" className="nw-done-ring" />
                <path d="M40 62 L55 77 L82 46" className="nw-done-tick" />
                {[0, 60, 120, 180, 240, 300].map((a) => (<path key={a} className="nw-done-spark" d={STAR} transform={`translate(60 60) rotate(${a}) translate(0 -54) scale(.7)`} />))}
              </svg>
              <h3>{done === "signup" ? "Welcome aboard." : "Sparkling clean."}</h3>
              <p>Opening your dashboard…</p>
            </div>
          ) : view === "welcome" ? (
            <div className="nw-pane nw-welcome" key="welcome" data-testid="welcome-pane">
              <AvatarRing name={first} progress={pinLen / 4} state={error ? "error" : loading ? "busy" : pinLen ? "typing" : "idle"} />
              <div className="nw-eyebrow nw-center"><ShieldCheck className="w-3.5 h-3.5" /> WELCOME BACK</div>
              <h3 className="nw-title nw-center nw-name-hl" data-testid="welcome-title"><span>{first}</span></h3>
              <p className="nw-who" data-testid="welcome-who">{hint.masked ? (hint.masked.includes("@") ? hint.masked : `+91 ${hint.masked}`) : hint.phone ? `+91 ${maskPhone(hint.phone)}` : maskEmail(hint.email || hint.identifier)}</p>
              {usePassword ? (
                <form className="nw-stack nw-pw" onSubmit={submitPassword} data-testid="admin-password-form">
                  <label className="nw-field"><span>Admin password</span><LockKeyhole className="nw-ico" />
                    <input className="nw-input" type="password" autoComplete="current-password" autoFocus value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} data-testid="admin-password-input" /></label>
                  <p className={error ? "nw-error" : "nw-visually-hidden"} role="alert">{error}</p>
                  <button type="submit" className="nw-cta" disabled={loading || !password} data-testid="admin-password-submit"><span>{loading ? <><i className="nw-spin" /> Signing in…</> : "Sign in"}</span></button>
                </form>
              ) : hint.hasPin === false ? (
                <div className="nw-nopin" data-testid="no-pin-note">
                  <p>You haven't created a PIN yet. We'll e-mail you a code to sign in, then you choose a 4-digit PIN.</p>
                  <button type="button" className="nw-cta" onClick={() => sendCode(null, "forgot")} disabled={busy} data-testid="no-pin-send"><span>{loading ? <><i className="nw-spin" /> Sending…</> : "E-mail me a code"}</span></button>
                  {error ? <p className="nw-error" role="alert">{error}</p> : null}
                </div>
              ) : (<>
                <PinPad ref={pinRef} value={pin} onChange={onLoginPin} disabled={busy} shake={shake} autoFocus testId="pin-pad" />
                <p key={liveLine} className={`nw-live${error ? " is-error" : ""}`} role={error ? "alert" : "status"} aria-live="polite" data-testid="pin-live">{liveLine}</p>
              </>)}
              <div className="nw-options" data-testid="login-options">
                {hint.hasPin !== false && !usePassword ? (<>
                  <button type="button" className="nw-opt" onClick={() => sendCode(null, "login")} disabled={busy} data-testid="login-email-code">
                    <Mail className="w-4 h-4" /><span><b>Log in with e-mail code</b><small>{hint.maskedEmail ? `We'll send a code to ${hint.maskedEmail}` : "We'll e-mail you a 6-digit code"}</small></span><ChevronRight className="w-4 h-4 nw-opt-go" />
                  </button>
                  <button type="button" className="nw-opt" onClick={() => sendCode(null, "forgot")} disabled={busy} data-testid="forgot-pin">
                    <KeyRound className="w-4 h-4" /><span><b>Forgot PIN?</b><small>Verify by e-mail, then choose a new PIN</small></span><ChevronRight className="w-4 h-4 nw-opt-go" />
                  </button>
                </>) : null}
                {hint.passwordAllowed ? (
                  <button type="button" className="nw-opt" onClick={() => { setUsePassword((v) => !v); setError(""); setPin(""); setPassword(""); }} data-testid="toggle-password">
                    <LockKeyhole className="w-4 h-4" /><span><b>{usePassword ? "Use my PIN instead" : "Use admin password instead"}</b></span><ChevronRight className="w-4 h-4 nw-opt-go" />
                  </button>
                ) : null}
                <button type="button" className="nw-opt nw-opt-quiet" onClick={() => { if (!hint.viaLookup) forgetReturning(); setHint(null); setIdentifier(""); go("login"); }} data-testid="switch-account">
                  <UserRound className="w-4 h-4" /><span><b>Log in with another account</b></span><ChevronRight className="w-4 h-4 nw-opt-go" />
                </button>
              </div>
            </div>
          ) : view === "code" ? (
            <div className="nw-pane" key="code" data-testid="otp-pane">
              <div className="nw-eyebrow"><ShieldCheck className="w-3.5 h-3.5" /> {codeIntent === "login" ? "SIGN IN WITH E-MAIL CODE" : "FORGOT PIN? SIGN IN WITH A CODE"}</div>
              <h3 className="nw-title">Enter your code.</h3>
              <p className="nw-sub" aria-live="polite">{info || "We sent a 6-digit code to the e-mail on your account."} It expires in 10 minutes.</p>
              <form className="nw-stack" onSubmit={(e) => { e.preventDefault(); verify(); }}>
                <label className="nw-field"><span>6-digit code</span><KeyRound className="nw-ico" />
                  <input ref={codeRef} data-testid="otp-input" className="nw-input nw-pin-input nw-code-input" inputMode="numeric" autoComplete="one-time-code"
                    pattern="[0-9]*" maxLength={6} value={code} onChange={onCodeChange} placeholder="••••••" aria-invalid={!!error} aria-describedby="nw-auth-msg" disabled={loading} />
                </label>
                <p id="nw-auth-msg" className={error ? "nw-error" : "nw-visually-hidden"} role="alert">{error}</p>
                <button type="submit" className="nw-cta" disabled={loading || code.length !== 6} data-testid="otp-verify"><span>{loading ? <><i className="nw-spin" /> Verifying…</> : "Verify & sign in"}</span></button>
                <button type="button" className="nw-ghost nw-resend" onClick={resend} disabled={loading || cooldown > 0} data-testid="otp-resend">
                  {cooldown > 0 ? <><ResendRing left={cooldown} total={RESEND_SECONDS} /> Resend code in {cooldown}s</> : "Resend code"}
                </button>
              </form>
              <button type="button" className="nw-link nw-back" onClick={() => go(hint ? "welcome" : "login")} data-testid="otp-back"><ArrowLeft className="w-4 h-4" /> Back to PIN</button>
            </div>
          ) : view === "signupPin" || view === "newPin" ? (
            <div className="nw-pane nw-pinview" key={view} data-testid="create-pin-pane">
              <div className="nw-eyebrow"><LockKeyhole className="w-3.5 h-3.5" /> {copy[view].eyebrow}</div>
              <h3 key={`t-${pinPhase}`} className="nw-title nw-swap" data-testid="create-pin-title">{copy[view].title}</h3>
              <p key={`sub-${pinPhase}`} className="nw-sub nw-swap">{copy[view].sub}</p>
              <div className="nw-steps-dots" aria-hidden="true"><i className={pinPhase === "create" ? "is-on" : "is-past"} /><i className={pinPhase === "confirm" ? "is-on" : ""} /></div>
              <PinPad ref={pinRef} value={pin} onChange={onNewPin} disabled={busy} shake={shake} autoFocus testId="new-pin-pad" label={pinPhase === "create" ? "Create a 4-digit PIN" : "Confirm your 4-digit PIN"} />
              <p key={`live-${pinPhase}-${liveLine}`} className={`nw-live${error ? " is-error" : ""}`} role={error ? "alert" : "status"} aria-live="polite">
                {error || (loading ? liveLine : pinPhase === "create" ? (pinLen ? stepTalk : "Choose 4 digits") : (pinLen ? stepTalk : "Type it once more"))}
              </p>
              {view === "signupPin" ? (
                <button type="button" className="nw-link nw-back" onClick={() => go("signup")} disabled={busy}><ArrowLeft className="w-4 h-4" /> Edit my details</button>
              ) : (
                <button type="button" className="nw-link nw-back nw-muted" onClick={() => finish("login")} disabled={busy} data-testid="skip-new-pin">Skip for now</button>
              )}
            </div>
          ) : view === "signup" ? (
            <div className="nw-pane" key="signup" data-testid="signup-pane">
              <div className="nw-eyebrow"><Sparkles className="w-3.5 h-3.5" /> {copy.signup.eyebrow}</div>
              <h3 className="nw-title" data-testid="auth-title">{copy.signup.title}</h3>
              <p className="nw-sub">{copy.signup.sub}</p>
              {googleClientId ? (<>
                <div className="nw-google" data-testid="google-signin-btn"><div ref={googleBtnRef} /></div>
                <div className="nw-or"><span>or with your mobile number</span></div>
              </>) : null}
              <form onSubmit={signupDetails} className="nw-stack" noValidate>
                <label className="nw-field"><span>Full name</span><User className="nw-ico" /><input data-testid="auth-name-input" className="nw-input" autoComplete="name" value={name} onChange={(e) => { setName(e.target.value); setError(""); }} /></label>
                <label className="nw-field"><span>Mobile number</span><Smartphone className="nw-ico" /><em className="nw-prefix">+91</em>
                  <input data-testid="auth-phone-input" className="nw-input nw-input-phone" type="tel" inputMode="numeric" autoComplete="tel-national" placeholder="98765 43210" value={phone} onChange={onPhoneChange} aria-invalid={!!error} /></label>
                <label className="nw-field"><span>E-mail</span><Mail className="nw-ico" /><input data-testid="auth-email-input" className="nw-input" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }} aria-invalid={!!error} /></label>
                <p className={error ? "nw-error" : "nw-visually-hidden"} role="alert">{error}</p>
                <button data-testid="auth-submit-btn" type="submit" className="nw-cta"><span>Continue &amp; create PIN</span></button>
              </form>
              <div className="nw-foot">
                <button type="button" className="nw-link" onClick={() => switchMode("signin")} data-testid="switch-to-login">Already have an account? <b>Log in</b></button>
                <div className="nw-trust"><ShieldCheck className="w-3.5 h-3.5" /> Your PIN is stored encrypted · Your details stay private</div>
              </div>
            </div>
          ) : (
            <div className="nw-pane" key="login" data-testid="login-pane">
              <div className="nw-eyebrow"><Sparkles className="w-3.5 h-3.5" /> {copy.login.eyebrow}</div>
              <h3 className="nw-title" data-testid="auth-title">{copy.login.title}</h3>
              <p className="nw-sub">{copy.login.sub}</p>
              {googleClientId ? (<>
                <div className="nw-google" data-testid="google-signin-btn"><div ref={googleBtnRef} /></div>
                <div className="nw-or"><span>or use your mobile number</span></div>
              </>) : null}
              <form className="nw-stack" onSubmit={lookup} noValidate>
                <label className="nw-field"><span>Mobile number or e-mail</span><Smartphone className="nw-ico" />
                  <input data-testid="auth-id-input" autoFocus name="username" autoComplete="username" autoCapitalize="none" spellCheck={false} className="nw-input" inputMode="text" placeholder="Mobile number or e-mail"
                    value={identifier} onChange={(e) => { setIdentifier(e.target.value); setError(""); }} aria-invalid={!!error} /></label>
                <p className={`nw-seeking${lookingUp ? " is-on" : ""}${notFound ? " is-miss" : ""}`} aria-live="polite" data-testid="login-status">
                  {lookingUp ? <><i className="nw-spin nw-spin-dark" /> Finding your account…</>
                    : notFound ? <>No account found. <button type="button" className="nw-link" onClick={() => switchMode("signup")}>Create one</button></>
                    : "Just type it. We find you and ask for your PIN. No Next button."}
                </p>
                <p className={error ? "nw-error" : "nw-visually-hidden"} role="alert" data-testid="login-error">{error}</p>
                <button type="submit" className="nw-visually-hidden" tabIndex={-1} aria-hidden="true" data-testid="login-continue">Continue</button>
              </form>
              <div className="nw-foot">
                <button type="button" className="nw-link" onClick={() => switchMode("signup")} data-testid="switch-to-signup">New to NAVI CARE? <b>Create account</b></button>
                <div className="nw-trust"><ShieldCheck className="w-3.5 h-3.5" /> Secure PIN sign-in · Your details stay private</div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>,
    document.body
  );
}
